import React, { useRef, useState, useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { analyzeText } from "../../components/KLA/process_transcription";
import domtoimage from "dom-to-image";
import jsPDF from "jspdf";
import KLAReportPage2 from "../../components/KLA/KLAReportPage2";
import KLAReportPage3 from "../../components/KLA/KLAReportPage3";
import KLAReportPage4 from "../../components/KLA/KLAReportPage4";
import KLAReportPage6 from "../../components/KLA/KLAReportPage6";
import KLAReportPage7 from "../../components/KLA/KLAReportPage7";
import KLAReportPage8 from "../../components/KLA/KLAReportPage8";
import KLAReportPage9 from "../../components/KLA/KLAReportPage9";
import { Col, Space, Modal, Spin, Button } from "antd";
import { CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import styled from "styled-components";
import { PageHeader } from "../../component/Header";
import { H4 } from "../../component/Typography";
import { PrimaryButton, SecondaryButton } from "../../component/Button";
import { useMediaQuery } from "react-responsive";

const formatDate = (dateString) => {
  if (!dateString) return null; // 날짜가 없으면 null 반환
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null; // 유효하지 않은 날짜 방지
  return date.toISOString().split("T")[0]; // YYYY-MM-DD 형식으로 변환
};

// 서버에 분석 결과와 PDF를 저장. 성공 시 { dataId }, 실패 시 throw.
const saveAnalysisToServer = async (
  userId,
  analysisData,
  inputText,
  pdfBlob
) => {
  const formData = new FormData();
  formData.append("user_id", userId);
  formData.append("client_name", analysisData.info.name);
  formData.append("client_birthday", formatDate(analysisData.info.birthday));
  formData.append("client_gender", analysisData.info.gender);
  formData.append("analysis_date", formatDate(analysisData.info.examdate));
  formData.append("input_text", inputText);
  formData.append("analysis_result", JSON.stringify(analysisData));
  formData.append("pdf_path", pdfBlob, `${analysisData.info.name}_report.pdf`);

  const response = await fetch(
    `${process.env.REACT_APP_SERVER_URL}/api/saveAnalysis`,
    {
      method: "POST",
      body: formData,
    }
  );

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.message || "저장에 실패했습니다.");
  }

  return result; // { dataId, ... }
};

function useForceUpdate() {
  const [, setState] = useState();
  return useCallback(() => setState({}), []);
}

function KLAResult() {
  const location = useLocation();
  const { inputText, user } = location.state || { inputText: "", user: null };

  const isMobile = useMediaQuery({ query: "(max-width: 576px)" });

  const [currentPage, setCurrentPage] = useState(1);
  const [subPage, setSubPage] = useState(1);
  const [analysis, setAnalysis] = useState({
    info: {
      name: "",
      gender: "",
      examdate: "",
      birthday: "",
      examiner: "",
      school: "",
      grade: "",
      region: "",
      institution: "",
      age_years: 0,
      age_months: 0,
      utteranceType: "",
    },
    mlu_w: [0, 0, 0, 0],
    mlu_m: [0, 0, 0, 0],
    num_gram_types: [0, 0, 0, 0],
    num_gram_tokens: [0, 0, 0, 0],
    num_morp_types: [0, 0, 0, 0],
    num_morp_tokens: [0, 0, 0, 0],
    ratio_gram: [0, 0, 0, 0],
    ratio_morp: [0, 0, 0, 0],
    num_word_types: [0, 0, 0, 0],
    num_word_tokens: [0, 0, 0, 0],
    ratio_word: [0, 0, 0, 0],
    num_line_total: [0, 0, 0, 0],
    num_line_unknown: [0, 0, 0, 0],
    num_line_partial: [0, 0, 0, 0],
    num_line_known: [0, 0, 0, 0],
    num_line_end_period: [0, 0, 0, 0],
    num_line_end_question: [0, 0, 0, 0],
    num_line_end_exclamation: [0, 0, 0, 0],
    num_line_end_bracket: [0, 0, 0, 0],
    num_line_end_exponential: [0, 0, 0, 0],
    statements: [[], [], []],
    statements_understand: [[], [], []],
    word_count: [[], [], []],
    wordlist: {},
    wordlist_count: {},
    grams_order_max: 0,
    grams_order: [[], [], []],
    grams_order_count: [[], [], []],
    tags: [],
    lineByTag: {},
    mlu_w_pos: 0.0,
    mlu_m_pos: 0.0,
    num_gram_types_pos: 0.0,
    num_graph_tokens_pos: 0.0,
    num_morp_types_pos: 0.0,
    num_morp_tokens_pos: 0.0,
    ratio_gram_pos: 0.0,
    ratio_morp_pos: 0.0,
    num_word_types_pos: 0.0,
    num_word_tokens_pos: 0.0,
    ratio_word_pos: 0.0,
  });
  const [isThumbnailView, setIsThumbnailView] = useState(false);
  const forceUpdate = useForceUpdate(); // 강제 렌더링을 위한 훅
  const [isExporting, setIsExporting] = useState(false);
  // 저장 진행 모달 상태. status: 'idle' | 'saving' | 'success' | 'error'
  const [saveModal, setSaveModal] = useState({ status: "idle" });

  const handleSetCurrentPage = (page) => {
    //console.log("Changing currentPage to:", page); // currentPage 변경 전에 확인
    setCurrentPage(page);
  };

  const handleSetSubPage = (page) => {
    //console.log("Changing subPage to:", page); // subPage 변경 전에 확인
    setSubPage(page);
  };

  useEffect(() => {
    if (inputText) {
      const result = analyzeText(inputText);
      setAnalysis(result);
    }
  }, [inputText]);

  const thumbnailRefs2 = useRef();
  const thumbnailRefs3 = useRef();
  const thumbnailRefs4 = useRef();
  const thumbnailRefs6 = useRef([]); // 배열 형태로 초기화
  const thumbnailRefs7 = useRef([]);
  const thumbnailRefs8 = useRef([]);
  const thumbnailRefs9 = useRef([]);

  const pageRefs2 = useRef();
  const pageRefs3 = useRef();
  const pageRefs4 = useRef();
  const pageRefs6 = useRef([]); // 배열 형태로 초기화
  const pageRefs7 = useRef([]);
  const pageRefs8 = useRef([]);
  const pageRefs9 = useRef([]);

  useEffect(() => {
    [
      thumbnailRefs6,
      thumbnailRefs7,
      thumbnailRefs8,
      thumbnailRefs9,
      pageRefs6,
      pageRefs7,
      pageRefs8,
      pageRefs9,
    ].forEach((ref) => {
      if (!Array.isArray(ref.current)) {
        ref.current = [];
      }
    });

    // 컴포넌트 언마운트 시 ref 배열 초기화
    return () => {
      pageRefs6.current = [];
      pageRefs7.current = [];
      pageRefs8.current = [];
      pageRefs9.current = [];
      thumbnailRefs6.current = [];
      thumbnailRefs7.current = [];
      thumbnailRefs8.current = [];
      thumbnailRefs9.current = [];
    };
  }, []);

  const generatePDF = async () => {
    setIsExporting(true);
    setIsThumbnailView(true);

    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = 210;

    await new Promise((resolve) => setTimeout(resolve, 500)); // 렌더링 대기
    forceUpdate(); // 강제 렌더링

    const allRefs = [
      pageRefs2.current,
      pageRefs3.current,
      ...pageRefs4.current, // ✅ 꼭 펼쳐야 함
      ...pageRefs6.current,
      ...pageRefs7.current,
      ...pageRefs8.current,
      ...pageRefs9.current,
    ].filter(Boolean); // null 제거

    try {
      for (let i = 0; i < allRefs.length; i++) {
        const ref = allRefs[i];

        // ✅ 캡처해서 이미지로 변환
        const dataUrl = await domtoimage.toJpeg(ref, {
          quality: 0.95,
          style: {
            transform: "scale(1)",
            transformOrigin: "top left",
            fontFamily: "Noto Sans KR",
          },
          width: ref.scrollWidth,
          height: ref.scrollHeight,
        });

        const img = new Image();
        img.src = dataUrl;

        await new Promise((resolve) => {
          img.onload = () => {
            const imgHeight = (img.height * pdfWidth) / img.width;

            if (i > 0) pdf.addPage();
            pdf.addImage(dataUrl, "JPEG", -5, -5, pdfWidth, imgHeight);
            resolve();
          };
        });
      }

      return pdf;
    } catch (err) {
      console.error("Error generating PDF:", err);
    } finally {
      setIsExporting(false);
      setIsThumbnailView(false); // 필요 시 복원
    }
  };

  const downloadPDF = async () => {
    const pdf = await generatePDF();
    if (pdf) {
      pdf.save("report.pdf");
    }
  };

  const handleSave = async () => {
    if (!user || !user.id) {
      setSaveModal({
        status: "error",
        message: "사용자 정보가 없어 저장할 수 없습니다. 다시 로그인해주세요.",
      });
      return;
    }

    setSaveModal({ status: "saving" });

    try {
      const pdf = await generatePDF();
      if (!pdf) {
        setSaveModal({
          status: "error",
          message: "PDF 생성에 실패했습니다.",
        });
        return;
      }
      const pdfBlob = pdf.output("blob");
      const result = await saveAnalysisToServer(
        user.id,
        analysis,
        inputText,
        pdfBlob
      );
      setSaveModal({ status: "success", dataId: result.dataId });
    } catch (err) {
      console.error("저장 오류:", err);
      setSaveModal({
        status: "error",
        message: err.message || "서버 요청에 실패했습니다. 네트워크 상태를 확인해주세요.",
      });
    }
  };

  const closeSaveModal = () => setSaveModal({ status: "idle" });

  return (
    <div>
      <PageHeader title="결과 보고서" />

      <IndicatorContainer isMobile={isMobile}>
        <SideComponent align="flex-start" style={{ width: "100%" }}>
          <H4>report.pdf</H4>
        </SideComponent>

        <SideComponent>
          <Space
            style={{
              width: "100%",
              justifyContent: "flex-end",
            }}
          >
            <SecondaryButton size="large" onClick={handleSave}>
              저장하기
            </SecondaryButton>
            <PrimaryButton size="large" onClick={downloadPDF}>
              PDF 내려받기
            </PrimaryButton>
          </Space>
        </SideComponent>
      </IndicatorContainer>

      <PageContainer>
        <Col
          span={isMobile ? 24 : 6}
          style={{
            maxWidth: isMobile ? "100%" : "300px",
          }}
        >
          <PreviewContainer
            isMobile={isMobile}
            scale={isMobile ? 0.4 : 0.3}
            direction="vertical"
          >
            <PreviewWrapper
              onClick={() => setCurrentPage(1)}
              //isSelected={currentPage === 1}
            >
              <KLAReportPage2 ref={thumbnailRefs2} customData={analysis} />
            </PreviewWrapper>
            <PreviewWrapper
              onClick={() => setCurrentPage(2)}
              //isSelected={currentPage === 2}
            >
              <KLAReportPage3 ref={thumbnailRefs3} customData={analysis} />
            </PreviewWrapper>

            <PreviewWrapper>
              <KLAReportPage4
                ref={thumbnailRefs4}
                customData={analysis}
                setCurrentPage={(page) => {
                  handleSetCurrentPage(3); // main 페이지 4으로 설정
                  handleSetSubPage(page); // subPage 값 변경
                }}
                isThumbnailView={true}
              />
            </PreviewWrapper>

            <PreviewWrapper>
              <KLAReportPage6
                ref={thumbnailRefs6}
                customData={analysis}
                setCurrentPage={(page) => {
                  handleSetCurrentPage(4); // main 페이지 4으로 설정
                  handleSetSubPage(page); // subPage 값 변경
                }}
                isThumbnailView={true}
              />
            </PreviewWrapper>
            <PreviewWrapper>
              <KLAReportPage7
                ref={thumbnailRefs7}
                customData={analysis}
                setCurrentPage={(page) => {
                  handleSetCurrentPage(5); // main 페이지 4으로 설정
                  handleSetSubPage(page); // subPage 값 변경
                }}
                isThumbnailView={true}
              />
            </PreviewWrapper>
            <PreviewWrapper>
              <KLAReportPage8
                ref={thumbnailRefs8}
                customData={analysis}
                setCurrentPage={(page) => {
                  handleSetCurrentPage(6); // main 페이지 4으로 설정
                  handleSetSubPage(page); // subPage 값 변경
                }}
                isThumbnailView={true}
              />
            </PreviewWrapper>
            <PreviewWrapper>
              <KLAReportPage9
                ref={thumbnailRefs9}
                customData={analysis}
                setCurrentPage={(page) => {
                  handleSetCurrentPage(7); // main 페이지 4으로 설정
                  handleSetSubPage(page); // subPage 값 변경
                }}
                isThumbnailView={true}
              />
            </PreviewWrapper>
          </PreviewContainer>
        </Col>

        <Col span={18} style={{ display: isMobile ? "none" : "block" }}>
          <DetailContainer>
            <DetailWrapper
              isSelect={currentPage === 1}
              isExporting={isExporting}
            >
              <KLAReportPage2 ref={pageRefs2} customData={analysis} />
            </DetailWrapper>
            <DetailWrapper
              isSelect={currentPage === 2}
              isExporting={isExporting}
            >
              <KLAReportPage3 ref={pageRefs3} customData={analysis} />
            </DetailWrapper>
            <DetailWrapper
              isSelect={currentPage === 3}
              isExporting={isExporting}
            >
              <KLAReportPage4
                ref={pageRefs4}
                customData={analysis}
                subPage={subPage}
                isThumbnailView={isThumbnailView}
              />
            </DetailWrapper>
            <DetailWrapper
              isSelect={currentPage === 4}
              isExporting={isExporting}
            >
              <KLAReportPage6
                ref={pageRefs6}
                customData={analysis}
                subPage={subPage}
                isThumbnailView={isThumbnailView}
              />
            </DetailWrapper>
            <DetailWrapper
              isSelect={currentPage === 5}
              isExporting={isExporting}
            >
              <KLAReportPage7
                ref={pageRefs7}
                customData={analysis}
                subPage={subPage}
                isThumbnailView={isThumbnailView}
              />
            </DetailWrapper>
            <DetailWrapper
              isSelect={currentPage === 6}
              isExporting={isExporting}
            >
              <KLAReportPage8
                ref={pageRefs8}
                customData={analysis}
                subPage={subPage}
                isThumbnailView={isThumbnailView}
              />
            </DetailWrapper>
            <DetailWrapper
              isSelect={currentPage === 7}
              isExporting={isExporting}
            >
              <KLAReportPage9
                ref={pageRefs9}
                customData={analysis}
                subPage={subPage}
                isThumbnailView={isThumbnailView}
              />
            </DetailWrapper>
          </DetailContainer>
        </Col>
      </PageContainer>

      {/* 저장 진행/완료 모달
          - saving: 닫기 불가, 스피너 + "저장 중..."
          - success: 완료 메시지 + 데이터 ID + [확인]
          - error: 오류 메시지 + [확인]                                            */}
      <Modal
        open={saveModal.status !== "idle"}
        closable={saveModal.status !== "saving"}
        maskClosable={false}
        keyboard={saveModal.status !== "saving"}
        onCancel={closeSaveModal}
        title={
          saveModal.status === "saving"
            ? "저장 중"
            : saveModal.status === "success"
            ? "저장 완료"
            : "저장 실패"
        }
        footer={
          saveModal.status === "saving" ? null : (
            <Button type="primary" onClick={closeSaveModal}>
              확인
            </Button>
          )
        }
      >
        {saveModal.status === "saving" && (
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <Spin size="large" />
            <p style={{ marginTop: 16, fontSize: 15 }}>
              저장 중입니다. 잠시만 기다려주세요...
            </p>
          </div>
        )}
        {saveModal.status === "success" && (
          <div style={{ padding: "8px 0", fontSize: 15 }}>
            <CheckCircleFilled style={{ color: "#52c41a", marginRight: 8 }} />
            저장이 완료되었습니다.
            {saveModal.dataId && (
              <div style={{ marginTop: 8, color: "#888", fontSize: 13 }}>
                데이터 ID: {saveModal.dataId}
              </div>
            )}
          </div>
        )}
        {saveModal.status === "error" && (
          <div style={{ padding: "8px 0", fontSize: 15 }}>
            <CloseCircleFilled style={{ color: "#ff4d4f", marginRight: 8 }} />
            {saveModal.message}
          </div>
        )}
      </Modal>
    </div>
  );
}

const IndicatorContainer = styled.div`
  display: flex;
  flex-direction: ${({ isMobile }) => (isMobile ? "column" : "row")};
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 16px 24px;
  box-sizing: border-box;
  background-color: var(--bg-disable);
  gap: 16px;
`;

const SideComponent = styled.div`
  flex: 1;
  display: flex;
  justify-content: ${({ align }) => align || "flex-start"};
  width: 100%;
`;

const PageContainer = styled.div`
  display: flex;
  max-width: 1280px;
  margin: 0 auto;
  gap: 16px;

  height: 100vh;

  overflow: scroll;
`;

const PreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  transform-origin: top left;
  transform: scale(${(props) => props.scale});
  width: calc(100% * (1 / ${(props) => props.scale}));
  align-items: center;
  height: ${({ scale, isMobile }) =>
    isMobile ? "100%" : (1123 + 120) * (1 / scale)}px;);
  overflow: ${({ isMobile }) => (isMobile ? "auto" : "scroll")};

  minwidth: 300px;
`;

const PreviewWrapper = styled.div`
  cursor: pointer;
  width: 100%;
  align-items: center;
  justify-content: center;
  display: flex;
  height: ${(props) => (1123 + 120) * (1 / props.scale)}px;
`;

const DetailContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const DetailWrapper = styled.div`
  display: flex;
  visibility: ${({ isExporting, isSelect }) =>
    isExporting || isSelect
      ? "visible"
      : "hidden"}; // Only visible when exporting or selected
  position: absolute; // Prevent layout shifts
`;

export default KLAResult;
